import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Check, CreditCard, LogIn, Sparkles } from "lucide-react";
import { RootState } from "../../../../redux/store";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import {
    useKeyHandoverSubscription,
    PaymentModelData,
} from "../context/KeyHandoverSubscriptionContext";

const MODEL_ICONS: Record<PaymentModelData["id"], typeof CreditCard> = {
    "single-booking": CreditCard,
    "subscription-existing": LogIn,
    "subscription-new": Sparkles,
};

function PaymentModelCard({
    model,
    isSelected,
    onSelect,
    className,
}: {
    model: PaymentModelData;
    isSelected: boolean;
    onSelect: () => void;
    className?: string;
}) {
    const { t } = useTranslation();
    const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
    const Icon = MODEL_ICONS[model.id];

    return (
        <button
            onClick={onSelect}
            className={`
                relative w-full p-5 transition-all text-left
                ${isSelected ? "bg-primary/5" : "bg-white hover:bg-slate-50"}
                ${className ?? ""}
            `}
        >
            <div className="flex items-start gap-4">
                <div className={`
                    flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                    ${isSelected ? "bg-primary text-white" : "bg-slate-100 text-slate-500"}
                `}>
                    <Icon className="h-5 w-5" />
                </div>

                <div className="flex-1 min-w-0 pr-8">
                    <div className="text-base font-semibold text-slate-900">
                        {t(model.titleKey)}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                        {t(model.descriptionKey)}
                    </div>
                    {model.lowestPrice && model.priceUnitKey && (
                        <div className="text-sm font-semibold text-primary mt-2">
                            {t("book_location.from")} {model.lowestPrice} {tenantConfig.currencySymbol} / {t(model.priceUnitKey)}
                        </div>
                    )}
                </div>
            </div>

            {isSelected && (
                <div className="absolute top-1/2 -translate-y-1/2 right-4">
                    <div className="bg-primary text-white rounded-full p-1">
                        <Check className="h-4 w-4" />
                    </div>
                </div>
            )}
        </button>
    );
}

function KeyHandoverPaymentModelStep() {
    const { t } = useTranslation();
    const { models, selected, isLoading, select, deselect } = useKeyHandoverSubscription();

    const singleBookingModel = useMemo(
        () => models.find((m) => m.id === "single-booking"),
        [models]
    );
    const subscriptionModels = useMemo(
        () => models.filter((m) => m.id !== "single-booking"),
        [models]
    );

    const isAnySubscriptionSelected = selected?.id.startsWith("subscription-") ?? false;

    const handleSelect = (model: PaymentModelData) => {
        if (selected?.id === model.id) {
            deselect();
        } else {
            select(model);
        }
    };

    return (
        <div
            className="flex flex-col gap-4 relative"
            role="radiogroup"
            aria-label={t("book_location.keys.subscription_model.header_text", {
                defaultValue: "Choose your payment model",
            })}
            aria-busy={isLoading}
        >
            {singleBookingModel && (
                <div className={`
                    rounded-xl border transition-all overflow-hidden
                    ${selected?.id === singleBookingModel.id
                        ? "border-primary ring-1 ring-primary/20 shadow-md"
                        : "border-slate-200"
                    }
                `}>
                    <PaymentModelCard
                        model={singleBookingModel}
                        isSelected={selected?.id === singleBookingModel.id}
                        onSelect={() => handleSelect(singleBookingModel)}
                    />
                </div>
            )}

            {subscriptionModels.length > 0 && (
                <div className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isAnySubscriptionSelected
                        ? "border-primary ring-1 ring-primary/20 shadow-md"
                        : "border-slate-200"
                    }
                `}>
                    {subscriptionModels.map((model, index) => (
                        <div key={model.id}>
                            {index > 0 && (
                                <div className="h-px bg-slate-200 mx-5" />
                            )}
                            <PaymentModelCard
                                model={model}
                                isSelected={selected?.id === model.id}
                                onSelect={() => handleSelect(model)}
                            />
                        </div>
                    ))}
                </div>
            )}

            <div aria-live="polite">
                <LoaderOverlay open={isLoading} />
            </div>
        </div>
    );
}

export default memo(KeyHandoverPaymentModelStep);
